import { API, apiUrl } from '@/config';
import type {
  AddSeatRequest,
  AddSeatResponse,
  ReleaseSeatResponse,
  CreateOrganizationRequest,
  CreateOrganizationResponse,
  CustomerDetailResponse,
  CustomerListQuery,
  CustomerListResponse,
  CustomerPortalUser,
  IssueLicenseRequest,
  IssueLicenseResponse,
  EmailCredentialsResponse,
  EmailPortalPasswordRequest,
  EmailSeatCredentialsRequest,
  ExtendLicenseRequest,
  ExtendLicenseResponse,
  ProvisionRequest,
  ProvisionResponse,
  ReissueSeatResponse,
  SeatHistoryResponse,
  ResetPasswordResponse,
  RevokeSeatRequest,
  RevokeSeatResponse,
  SalesRep,
} from '@/types';
import { api, ApiError } from './client';
import { getAccessToken } from './tokens';

/**
 * Everything the staff console does to customers.
 *
 * A thin layer over the client, kept separate so components never build URLs or
 * know which endpoint does what.
 */

/**
 * The list endpoint answers `{ data, meta }`, and the shared client unwraps
 * `data` for every caller - which loses the pagination metadata.
 *
 * So this one call bypasses `api.get` and reads the envelope itself. Changing
 * the client to hand back the whole envelope would push that shape onto every
 * other call site for the sake of one, and dropping `meta` would leave the table
 * unable to say whether another page exists.
 */
async function listCustomers(
  query: CustomerListQuery,
  signal?: AbortSignal,
): Promise<CustomerListResponse> {
  const params = new URLSearchParams();
  if (query.limit !== undefined) params.set('limit', String(query.limit));
  if (query.offset !== undefined) params.set('offset', String(query.offset));
  if (query.search) params.set('search', query.search);
  if (query.orgType) params.set('orgType', query.orgType);
  if (query.status) params.set('status', query.status);

  const suffix = params.size > 0 ? `?${params.toString()}` : '';
  const token = getAccessToken();

  const response = await fetch(apiUrl(`${API.endpoints.customers}${suffix}`), {
    headers: token ? { authorization: `Bearer ${token}` } : {},
    credentials: 'include',
    signal: signal ?? AbortSignal.timeout(API.timeoutMs),
  }).catch(() => null);

  if (!response) throw new ApiError(0, null, 'Could not reach the server.');
  if (!response.ok) {
    const body = (await response.json().catch(() => null)) as null;
    throw new ApiError(response.status, body, 'Could not load customers.');
  }

  return (await response.json()) as CustomerListResponse;
}

/**
 * Every customer matching `query`, paged through until the server says stop.
 *
 * For the CSV export, which has to contain the whole result set rather than
 * whichever twenty rows the table happens to be showing. The list endpoint
 * caps `limit` at 100, so a few hundred labs is several requests no matter
 * how this is written - and they have to be sequential, because how many
 * pages there are is not known until the first one answers.
 */
const EXPORT_PAGE = 100;

/**
 * A server that always answered `hasMore` would otherwise loop forever,
 * fetching until the tab died. Ten thousand customers is far past anything
 * this product will hold and far short of anything that hurts to hold in
 * memory, which is what makes it a safe place to give up.
 */
const EXPORT_CEILING = 10_000;

async function listAllCustomers(
  query: Omit<CustomerListQuery, 'limit' | 'offset'>,
  signal?: AbortSignal,
): Promise<CustomerListResponse['data']> {
  const items: CustomerListResponse['data'] = [];

  for (let offset = 0; ; offset += EXPORT_PAGE) {
    const page = await listCustomers({ ...query, limit: EXPORT_PAGE, offset }, signal);
    items.push(...page.data);
    if (!page.meta.hasMore || items.length >= EXPORT_CEILING) return items;
  }
}

export const customersService = {
  list: listCustomers,

  /** The whole result set, paged through. See listAllCustomers. */
  listAll: listAllCustomers,

  get: (id: string, signal?: AbortSignal): Promise<CustomerDetailResponse> =>
    api.get<CustomerDetailResponse>(API.endpoints.customer(id), signal ? { signal } : {}),

  /** One seat's devices and licence events, newest first. `limit` pages the events only. */
  seatHistory: (
    organizationId: string,
    seatId: string,
    limit: number,
    signal?: AbortSignal,
  ): Promise<SeatHistoryResponse> =>
    api.get<SeatHistoryResponse>(
      `${API.endpoints.seatHistory(organizationId, seatId)}?limit=${String(limit)}`,
      signal ? { signal } : {},
    ),

  provision: (body: ProvisionRequest): Promise<ProvisionResponse> =>
    api.post<ProvisionResponse>(API.endpoints.customers, body),

  /** Just the organization - no licence, no seats. Shows up "not activated". */
  createOrganization: (body: CreateOrganizationRequest): Promise<CreateOrganizationResponse> =>
    api.post<CreateOrganizationResponse>(API.endpoints.createOrganization, body),

  /** Every active staff account, for the sales-rep picker. */
  listSalesReps: (signal?: AbortSignal): Promise<SalesRep[]> =>
    api.get<SalesRep[]>(API.endpoints.salesReps, signal ? { signal } : {}),

  /** A second (or third) licence for an organization that already has one. */
  issueLicense: (
    organizationId: string,
    body: IssueLicenseRequest,
  ): Promise<IssueLicenseResponse> =>
    api.post<IssueLicenseResponse>(API.endpoints.issueLicense(organizationId), body),

  /** Claims one of a licence's unused seats. 409 once they are all taken. */
  addSeat: (
    organizationId: string,
    licenseId: string,
    body: AddSeatRequest,
  ): Promise<AddSeatResponse> =>
    api.post<AddSeatResponse>(API.endpoints.addSeat(organizationId, licenseId), body),

  /**
   * Unbind a seat from the computer it was activated on.
   *
   * One machine holds one seat, enforced by a UNIQUE on the fingerprint, and
   * this is the only thing that lifts it - a workstation that was replaced or
   * reimaged cannot activate anything until somebody calls this.
   */
  releaseSeat: (organizationId: string, seatId: string): Promise<ReleaseSeatResponse> =>
    api.post<ReleaseSeatResponse>(API.endpoints.releaseSeat(organizationId, seatId), undefined),

  /**
   * Take a seat back. `seat` keeps the row and the person on it, ready for a
   * replacement key; `seat_and_user` deletes both, and is refused by the server
   * once a device has ever activated the seat.
   */
  revokeSeat: (
    organizationId: string,
    seatId: string,
    body: RevokeSeatRequest,
  ): Promise<RevokeSeatResponse> =>
    api.post<RevokeSeatResponse>(API.endpoints.revokeSeat(organizationId, seatId), body),

  /** A replacement key on an existing seat, keeping whatever term it had. */
  reissueSeat: (organizationId: string, seatId: string): Promise<ReissueSeatResponse> =>
    api.post<ReissueSeatResponse>(API.endpoints.reissueSeat(organizationId, seatId), undefined),

  /** Pushes every still-live seat on a licence forward by N months. */
  extendLicense: (
    organizationId: string,
    licenseId: string,
    body: ExtendLicenseRequest,
  ): Promise<ExtendLicenseResponse> =>
    api.post<ExtendLicenseResponse>(API.endpoints.extendLicense(organizationId, licenseId), body),

  /**
   * Send the seat holder their licence key, and the password with it when one
   * was just generated.
   *
   * There is deliberately no recipient parameter: the server reads the address
   * off the seat. Passing one would make this a way to send credential-shaped
   * mail from a verified NuDent identity to any address at all.
   */
  emailSeatCredentials: (
    organizationId: string,
    seatId: string,
    body: EmailSeatCredentialsRequest,
  ): Promise<EmailCredentialsResponse> =>
    api.post<EmailCredentialsResponse>(
      API.endpoints.emailSeatCredentials(organizationId, seatId),
      body,
    ),

  /** The same for a reset password, which belongs to a login not a seat. */
  emailPortalPassword: (
    portalUserId: string,
    body: EmailPortalPasswordRequest,
  ): Promise<EmailCredentialsResponse> =>
    api.post<EmailCredentialsResponse>(API.endpoints.emailPortalPassword(portalUserId), body),

  setPortalUserStatus: (
    portalUserId: string,
    status: CustomerPortalUser['status'],
  ): Promise<{ id: string; status: CustomerPortalUser['status'] }> =>
    api.patch(API.endpoints.portalUserStatus(portalUserId), { status }),

  /**
   * Omitting the body asks the server to generate one and return it once.
   *
   * `undefined` rather than `{}` on purpose: the endpoint accepts no body at
   * all, and sending an empty object would work but says less about intent.
   */
  resetPortalUserPassword: (
    portalUserId: string,
    password?: string,
  ): Promise<ResetPasswordResponse> =>
    api.post<ResetPasswordResponse>(
      API.endpoints.portalUserResetPassword(portalUserId),
      password === undefined ? undefined : { password },
    ),
};
